import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GraduationCap, Search } from 'lucide-react';
import AppContext from 'contexts/AppContext';
import { hasRole, ROLES } from 'utils/roleUtils';
import { ApiError } from 'utils/unwrapResponse';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Skeleton } from 'components/ui/skeleton';
import { EmptyState } from 'components/EmptyState';
import { useQueryClient } from '@tanstack/react-query';
import { TUTORIALS_QUERY_KEY, useTutorials } from 'hooks/useTutorials';
import { OTHER_TUTORIAL_CATEGORY, type TutorialVideo } from 'api/tutorialsApi';
import PillSelect from 'pages/AdminAudit/PillSelect';
import TutorialRow from './TutorialRow';
import TutorialPlayerDialog from './TutorialPlayerDialog';
import { matchesSearch, tutorialCategoryLabel } from './utils';
import UploadTutorialDialog from './UploadTutorialDialog';
import DeleteTutorialDialog from './DeleteTutorialDialog';
import EditTutorialDialog from './EditTutorialDialog';

const AdminTutorials = () => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AppContext);
  const canManage = hasRole(currentUser?.role, ROLES.SUPERADMIN);
  const { data, isLoading, isError, error, refetch } = useTutorials();
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [videoToEdit, setVideoToEdit] = useState<TutorialVideo | null>(null);
  const [videoToDelete, setVideoToDelete] = useState<TutorialVideo | null>(null);
  const [search, setSearch] = useState('');
  const [chosenCategory, setChosenCategory] = useState('');
  const [playingId, setPlayingId] = useState<string | null>(null);

  const sections = (data ?? []).filter(({ videos }) => videos.length > 0);
  const category = sections.some(({ key }) => key === chosenCategory) ? chosenCategory : '';
  const visibleSections = sections
    .filter(({ key }) => !category || key === category)
    .map((section) => ({ ...section, videos: section.videos.filter((video) => matchesSearch(video, search)) }))
    .filter(({ videos }) => videos.length > 0);
  const playing = (data ?? []).flatMap(({ videos }) => videos).find(({ id }) => id === playingId) ?? null;
  const filtering = search.trim() !== '' || category !== '';
  const uploadCategories = (data ?? []).map(({ key }) => key).filter((key) => key !== OTHER_TUTORIAL_CATEGORY);
  const reload = () => queryClient.invalidateQueries({ queryKey: TUTORIALS_QUERY_KEY });
  const notConfigured = error instanceof ApiError && error.status === 503;

  const resetFilters = () => {
    setSearch('');
    setChosenCategory('');
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="divide-y rounded-lg border bg-card" aria-busy="true">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="flex items-center gap-3 p-3">
              <Skeleton className="aspect-video w-28 shrink-0 rounded-md sm:w-40" />
              <div className="flex flex-1 flex-col gap-2">
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </div>
          ))}
        </div>
      );
    }
    if (isError && !data) {
      return notConfigured ? (
        <EmptyState title={t('Tutorials are not configured on this environment')} icon={GraduationCap} />
      ) : (
        <div role="alert" className="flex flex-col items-center gap-3 py-[54px] text-center">
          <p className="text-sm font-medium">{t("Couldn't load the tutorials")}</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            {t('Retry')}
          </Button>
        </div>
      );
    }
    if (!sections.length) return <EmptyState title={t('No tutorial yet')} icon={GraduationCap} />;
    if (!visibleSections.length) {
      return (
        <div className="flex flex-col items-center">
          <EmptyState title={t('No tutorial matches your search')} />
          <Button variant="outline" size="sm" onClick={resetFilters}>
            {t('Reset')}
          </Button>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-8">
        {visibleSections.map(({ key, videos }) => (
          <section key={key}>
            <h2 className="mb-3 text-base font-semibold">{tutorialCategoryLabel(t, key)}</h2>
            <ul className="divide-y rounded-lg border bg-card">
              {videos.map((video) => (
                <TutorialRow
                  key={video.id}
                  video={video}
                  canManage={canManage}
                  onPlay={setPlayingId}
                  onEdit={setVideoToEdit}
                  onDelete={setVideoToDelete}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-6 p-4 sm:p-6">
      <header className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">{t('Tutorials')}</h1>
        {canManage && uploadCategories.length > 0 && <Button onClick={() => setUploadOpen(true)}>{t('Add a tutorial')}</Button>}
      </header>
      {sections.length > 0 && (
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-[11px] top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              className="h-[38px] rounded-[9px] pl-[35px]"
              aria-label={t('Search a tutorial')}
              placeholder={t('Search by title or description…')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <PillSelect label={t('Category')} value={category} onChange={setChosenCategory}>
            <option value="">{t('All categories')}</option>
            {sections.map(({ key }) => (
              <option key={key} value={key}>
                {tutorialCategoryLabel(t, key)}
              </option>
            ))}
          </PillSelect>
          {filtering && (
            <button type="button" onClick={resetFilters} className="text-[13px] font-medium text-muted-foreground underline hover:text-foreground">
              {t('Reset')}
            </button>
          )}
        </div>
      )}
      {renderBody()}
      <TutorialPlayerDialog video={playing} onClose={() => setPlayingId(null)} onReload={() => refetch()} />
      {canManage && (
        <>
          <UploadTutorialDialog
            open={uploadOpen}
            onOpenChange={setUploadOpen}
            categories={uploadCategories}
            onUploaded={() => {
              // An active search could hide the row just added
              resetFilters();
              reload();
            }}
          />
          {videoToEdit && (
            <EditTutorialDialog
              video={videoToEdit}
              onClose={() => setVideoToEdit(null)}
              onSaved={() => {
                // A search on the old title would hide the row just renamed
                resetFilters();
                setVideoToEdit(null);
              }}
            />
          )}
          <DeleteTutorialDialog
            video={videoToDelete}
            onClose={() => setVideoToDelete(null)}
            onDeleted={() => {
              setVideoToDelete(null);
              reload();
            }}
          />
        </>
      )}
    </div>
  );
};

export default AdminTutorials;
